/**
 * Sexy Hair — desktop drag-to-scroll for Dawn <slider-component> tracks (WEB-6543)
 *
 * Every Sexy Hair carousel is a native scroll track: a <ul class="slider"> with
 * `overflow-x: auto` and `scroll-snap-type: x mandatory`, moved by writing
 * `scrollLeft`. Arrows, dots, trackpad and touch all work. Native overflow
 * scrolling has no click-and-drag, so a mouse press did nothing. This adds it.
 *
 * WHY THIS FILE FINDS ITS OWN TARGETS
 *
 * The tracks are wired three different ways. Some load sexyhair-slider-dots.js
 * and carry a <sexyhair-slider-dots> element; the Professionals rows load the
 * script but have arrows only and no dots element; Education On Demand and every
 * blog-article carousel load no brand script at all. Binding to the dots element
 * would reach only the first group. Binding to `slider-component .slider` and
 * gating per press (see below) reaches all three, and needs no section edits.
 *
 * THE GATE IS READ LIVE AT EVERY pointerdown, NEVER CACHED
 *
 * A track is draggable only while it BOTH computes to `overflow-x: auto|scroll`
 * AND actually has something to scroll (see canDrag). Reading that per press
 * rather than caching it at init does four jobs at once:
 *
 *   1. The home hero is `overflow-x: hidden` ON PURPOSE — it is an arrow-only
 *      slider, not a free-scrolling strip (see sexyhair-component-home-hero.css).
 *      `scrollLeft` writes still work on a hidden container, so a handler that
 *      bound blindly would silently hand it back the drag that CSS removed.
 *      The gate skips it with no name list to keep in sync.
 *   2. Dawn's `.slider--mobile` / `.slider--tablet` tracks compute to `auto` only
 *      below their breakpoints, so the gate follows the viewport for free.
 *   3. A track that gains or loses overflow on resize is handled, no listener.
 *   4. Nothing is cached, so a section re-render cannot leave a stale decision.
 *
 * See themes/sexyhair/CLAUDE.md → "Desktop drag vs click" for the trap below.
 */
(function () {
  'use strict';

  /* Registering twice would double every scrollLeft write and suppress clicks
     twice. The script is loaded once from theme.liquid, but guard anyway. */
  if (window.__sexyhairDragScrollInit) return;
  window.__sexyhairDragScrollInit = true;

  /* Dawn also ships <slideshow-component>. The only one in this theme wraps the
     PDP complementary-products block, which `templates/product.json` does not
     include — it never renders, and the served PDP contains no such element. It
     is left out rather than covered speculatively. If that block is ever added
     to the product template, extend this to 'slider-component .slider,
     slideshow-component .slider' and re-run the drag checks on the PDP. */
  var TRACK_SELECTOR = 'slider-component .slider';
  var READY_CLASS = 'sexyhair-drag-scroll';
  var GRAB_CLASS = 'sexyhair-drag-scroll--grabbing';
  var THRESHOLD = 5;

  /* ONE document-level listener pair for the whole page, not one pair per track.
     Only one pointer drag can be active at a time, so a single reference to the
     active gesture's end handler is enough. Registering per track instead left
     two listeners per track alive for the life of the document and pinned the
     detached track after every theme-editor re-render. */
  var activeEnd = null;

  /* Both halves matter, and the second one is not a nicety.
     `overflow-x` alone lets through a track that CAN scroll but has nothing to
     scroll — Pairs Well With is `overflow-x: auto` with three cards that fit
     exactly, and the Professionals artist rows are two slides in a two-up row.
     Binding those made a horizontal mouse drag across a card set `moved`, which
     suppressed the click on release: the card link stopped working and nothing
     scrolled to show why. Measured on a PDP before this guard existed. */
  function canDrag(track) {
    var overflowX = window.getComputedStyle(track).overflowX;
    if (overflowX !== 'auto' && overflowX !== 'scroll') return false;
    return track.scrollWidth - track.clientWidth > 1;
  }

  /* Deliberately NOT matching <a>. On most of these tracks the whole card IS a
     link, so guarding on anchors would kill drag everywhere it matters. A drag
     that lands on a link is neutralised by onClickCapture instead. Controls are
     matched, so a press that starts on an arrow or a form field never drags. */
  function isInteractiveTarget(target) {
    if (!target || !target.closest) return false;
    return !!target.closest(
      '.slider-button, button, input, select, textarea, [contenteditable="true"]'
    );
  }

  function initTrack(track) {
    var isDown = false;
    var startX = 0;
    var startY = 0;
    var startScrollLeft = 0;
    var dragKind = null;
    var moved = false;
    var activePointerId = null;
    var targetScrollLeft = 0;
    var rafId = null;
    var snapSuspended = false;
    var prevSnapType = '';
    var prevScrollBehavior = '';
    var prevUserSelect = '';
    var prevWebkitUserSelect = '';

    function applyScroll() {
      track.scrollLeft = targetScrollLeft;
      rafId = null;
    }

    /* Inline writes, not a CSS class. Every brand stylesheet that styles one
       of these tracks sets scroll-snap-type or scroll-behavior on it, at
       (0,1,0) or (0,2,0), and two of them — about-story and home-hero — carry
       no .sexyhair-component ancestor. An inline style beats all of them
       without a specificity contest or a load-order dependency. No count is
       given on purpose: two earlier revisions quoted one and both were wrong.

       The `snapSuspended` flag makes this idempotent. Without it a second call
       inside one gesture captured the value this function had just written, and
       restoreSnap then wrote `scroll-snap-type: none` onto the track
       permanently. Measured: snap stayed dead for the rest of the page session. */
    function suspendSnap() {
      if (snapSuspended) return;
      snapSuspended = true;
      prevSnapType = track.style.scrollSnapType;
      prevScrollBehavior = track.style.scrollBehavior;
      prevUserSelect = track.style.userSelect;
      prevWebkitUserSelect = track.style.webkitUserSelect;
      track.style.scrollSnapType = 'none';
      /* These tracks set `scroll-behavior: smooth` so the ARROWS animate. Left
         on during a drag it animates every frame's scrollLeft write, and the
         track visibly lags the pointer instead of tracking it. */
      track.style.scrollBehavior = 'auto';
      /* Inline rather than from the stylesheet: the cursor sheet loads with the
         `media="print" onload` swap, so its user-select rule is absent for the
         first moments of the page and a drag started then selects text. */
      track.style.userSelect = 'none';
      track.style.webkitUserSelect = 'none';
    }

    function restoreSnap() {
      if (!snapSuspended) return;
      snapSuspended = false;
      track.style.scrollSnapType = prevSnapType;
      track.style.scrollBehavior = prevScrollBehavior;
      track.style.userSelect = prevUserSelect;
      track.style.webkitUserSelect = prevWebkitUserSelect;
    }

    function onPointerDown(e) {
      /* Mouse only. Touch already pans the track natively and a second handler
         there fights the native scroller; pen behaves like touch. */
      if (e.pointerType !== 'mouse') return;
      if (e.button !== 0) return;
      /* A second press while a gesture is still open would overwrite startX and
         activePointerId and strand the first gesture's state. */
      if (isDown) return;
      if (!canDrag(track)) return;
      if (isInteractiveTarget(e.target)) return;

      isDown = true;
      moved = false;
      dragKind = null;
      startX = e.clientX;
      startY = e.clientY;
      startScrollLeft = track.scrollLeft;
      targetScrollLeft = startScrollLeft;
      activePointerId = e.pointerId;
      activeEnd = onPointerEnd;
      track.classList.add(GRAB_CLASS);
    }

    function onPointerMove(e) {
      if (!isDown || e.pointerId !== activePointerId) return;

      var dx = e.clientX - startX;
      var dy = e.clientY - startY;

      if (!dragKind && (Math.abs(dx) > THRESHOLD || Math.abs(dy) > THRESHOLD)) {
        dragKind = Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical';
        moved = true;
        if (dragKind === 'horizontal') {
          /* ⚠️ Capture HERE and nowhere else. Calling setPointerCapture in
             pointerdown retargets pointerup and click to the track, which
             silently kills every card link on desktop. Touch masks it, because
             a mobile click synthesizes from touch events rather than from the
             captured pointer. This is the WEB-6348 bug recorded in
             themes/kenra/CLAUDE.md → "Desktop drag vs click". */
          try {
            track.setPointerCapture(e.pointerId);
          } catch (_) {
            /* The call throws when the pointer is no longer active. The drag
               still works, but pointermove is bound to the TRACK, so without
               capture it stops the moment the pointer leaves the track's own
               bounds rather than the window's. */
          }
          suspendSnap();
        }
      }

      if (dragKind !== 'horizontal') return;

      /* Stops the browser turning the drag into a text selection. */
      e.preventDefault();
      targetScrollLeft = startScrollLeft - dx;
      if (rafId === null) rafId = window.requestAnimationFrame(applyScroll);
    }

    function onPointerEnd(e) {
      if (!isDown) return;
      if (e && activePointerId !== null && e.pointerId !== activePointerId) return;

      isDown = false;
      activeEnd = null;
      track.classList.remove(GRAB_CLASS);

      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
        rafId = null;
      }

      /* Keyed on snapSuspended, NOT on dragKind. A click that arrives
         mid-gesture clears dragKind, so dragKind can be null here while a
         horizontal drag really did run. Keying on dragKind skipped both the
         flush below and the restore, which lost up to one frame of drag
         distance and left the inline styles in place. */
      if (snapSuspended) {
        /* Flush the frame that the cancelled rAF would have written. */
        track.scrollLeft = targetScrollLeft;
        /* Restoring `scroll-snap-type` is the whole settle, and the browser is
           the only thing that knows each track's `scroll-snap-align`:
           Education On Demand's slides are `center`-aligned while every other
           track is `start`-aligned.

           MEASURED IN CHROMIUM on all four in-scope surfaces, not specified.
           CSS Scroll Snap §4.1.3 requires a re-snap when "the content or layout
           of the document changes"; a change to the scroll-snap-type VALUE is
           not a listed trigger, so this is user-agent behaviour. Re-check in
           Safari and Firefox before relying on it there.

           An explicit scrollTo to the nearest slide's LEFT edge was measured
           settling the start-aligned tracks and then being dragged back by the
           browser on the centre-aligned one — it computed 353 where the correct
           snap point was 454. Do not reintroduce one. */
        restoreSnap();
      }

      if (activePointerId !== null) {
        try {
          track.releasePointerCapture(activePointerId);
        } catch (_) {
          /* Already released — nothing to undo. */
        }
        activePointerId = null;
      }

      /* Backstop: clear the drag flags once the post-drag synthetic click — if
         the browser sends one — has already fired. A real click runs before
         this timeout, so suppression still works.

         Without it an interrupted drag leaves `moved` set, because
         onClickCapture is the only other place that clears it and no click
         arrives. The next click that is NOT preceded by a pointerdown — a
         keyboard Enter on a focused card link — is then swallowed as though it
         belonged to the old drag. Measured: after a `pointercancel` the card
         link stopped activating entirely, while the same click on a clean page
         navigated. Mirrors the backstop in
         themes/kenra/sections/kenra-color-hero.liquid. */
      window.setTimeout(function () {
        moved = false;
        dragKind = null;
      }, 0);
    }

    /* Capture phase, so the card's own link never sees a post-drag click.

       preventDefault ONLY. Cancelling the default action is what stops the
       card link navigating, and that is the whole goal.

       Do not add stopPropagation, even though
       themes/kenra/sections/kenra-best-sellers.liquid has it. A drag on a bound
       track DOES dispatch a click — this script cancels `dragstart` and sets
       draggable="false" on images, so there is no native drag to eat it — and
       stopPropagation then hides that click from every document-level listener.
       Measured: with it, a capture listener on the track sees the click and no
       document listener does; without it, the click reaches document normally.

       An earlier round claimed the opposite, from a control that dragged an
       unbound LINK. That case is not comparable: Chromium starts a native link
       drag there, and the native drag is what eats the click. The claim was
       wrong and is corrected here.

       The trade-off is real but small: without stopPropagation a delegated
       analytics handler can see a post-drag click on a card. Hiding clicks from
       every document listener is the larger hazard, and the visible symptom it
       was blamed for is masked anyway — the nav closes on mouseleave when the
       pointer travels to the carousel.

       The flags are cleared INSIDE the branch. Clearing them unconditionally
       let a click that arrived mid-gesture reset dragKind while the drag was
       still open, which re-entered suspendSnap and stranded
       `scroll-snap-type: none` on the track. */
    function onClickCapture(e) {
      if (moved && dragKind === 'horizontal') {
        e.preventDefault();
        moved = false;
        dragKind = null;
      }
    }

    track.addEventListener('pointerenter', function () {
      refreshAffordance(track);
    });
    track.addEventListener('pointerdown', onPointerDown);
    track.addEventListener('pointermove', onPointerMove);
    track.addEventListener('pointerup', onPointerEnd);
    track.addEventListener('pointercancel', onPointerEnd);
    track.addEventListener('lostpointercapture', onPointerEnd);
    track.addEventListener('click', onClickCapture, true);

    /* Without this the browser's native image drag starts instead of ours. */
    track.addEventListener('dragstart', function (e) {
      e.preventDefault();
    });
    track.querySelectorAll('img').forEach(function (img) {
      img.setAttribute('draggable', 'false');
    });
  }

  /* The grab cursor is a promise that a drag will do something, so it is driven
     by the same predicate as the drag itself. Refreshed on pointerenter because
     overflow changes with the viewport and with late-loading images, and
     pointerenter is exactly when the cursor starts to matter. */
  function refreshAffordance(track) {
    track.classList.toggle(READY_CLASS, canDrag(track));
  }

  /* The track gets pointer capture only AFTER the threshold is crossed, so a
     press that moves less than 5px and then leaves the track releases over a
     different element and never reaches the track's own listeners. `isDown` and
     the grabbing class would stay set and the cursor would stick at `grabbing`.
     Registered once for the document, and it ends only the active gesture. */
  function onDocumentPointerEnd(e) {
    if (activeEnd) activeEnd(e);
  }
  document.addEventListener('pointerup', onDocumentPointerEnd);
  document.addEventListener('pointercancel', onDocumentPointerEnd);

  /* Two passes, deliberately. canDrag reads getComputedStyle and scrollWidth,
     which both force a style and layout flush, and classList/dataset are
     writes that invalidate them again. Interleaving the two in one loop made
     every track after the first pay for a fresh synchronous reflow. A blog
     article carries up to four tracks. sexyhair-facets.js#runFilterTransition
     batches its reads the same way for the same reason. */
  function initAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var pending = [];

    scope.querySelectorAll(TRACK_SELECTOR).forEach(function (track) {
      if (track.dataset.sexyhairDragScroll === 'true') return;
      track.dataset.sexyhairDragScroll = 'true';
      initTrack(track);
      pending.push(track);
    });

    /* Pass 1 — read every gate with no writes in between. */
    var draggable = pending.map(canDrag);
    /* Pass 2 — apply every class with no reads in between. */
    pending.forEach(function (track, i) {
      track.classList.toggle(READY_CLASS, draggable[i]);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  /* Theme-editor re-renders replace a section's DOM, so re-scan that subtree.
     The document listeners above are registered once for the page, so a
     re-render adds no listener and strands no detached track. */
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target || document);
  });
})();
